 
/* eslint-disable max-len */
/* eslint  indent: ["warn",4,{ "ignoredNodes": ["TemplateLiteral"] }] */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import Moment from 'moment';
import { Button, CircularProgress, Grid, MenuItem, Paper, Drawer, Divider, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import * as yup from 'yup';
import WobiLogo from 'wobi-web-common/dist/assets/img/wobi-logo.svg';
import { validIsraeliId } from 'wobi-web-common/dist/components/utils/validation';
import { setSimulatorOffersFields } from '../../utils/persistOfferDetails';
import { getAgentLogo } from '../../config/agent';
import Offer from '../../components/OfferSimulator';
import SmallSelect from '../../components/SimulatorComponents/SmallSelect';
import ModalContainer from '../../components/SimulatorComponents/ModalContainer';
import { ProductType } from '../../enums';
import { checkFullCoverIsProtected } from '../../utils/common';
import { getFollowUpCallRandomDate } from '../../utils/apiHandlers';
import { conditions } from '../../utils/formConditions';
import GeneralDetails from './GeneralAutoDetails/GeneralDetails';
import AutoInfoDetails from './GeneralAutoDetails/AutoInfoDetails';
import useStyles from './useStyles';
import DriversDetails from './DriversDetails/DriversDetails';
import PassedInsurance from './DriversDetails/PassedInsurance';
import Claims from './DriversDetails/Claims';
import MoreDetails from './MoreDetails';
import FollowUpCall from './FollowUpCall';

const policyTypes = [
    {
        text: 'מקיף',
        value: 'FULLCOVER',
    },
    {
        text: 'צד ג׳',
        value: '3RDPARTY',
    },
];

const ALLOW_FIELDS_CHANGE_AFTER_GET_OFFER = {
    firstName: true,
    followupCallDate: true,
    followupPhoneNumber: true,
    israeliId: true,
    isSaleInProgress: true,
    lastName: true,
};

const categorizeOffers = (offers) => {
    const categorized = {
        discounted: [],
        mileage: [],
        regular: [],
    };

    offers.forEach((offer, originalIndex) => {
        const groups = offer.groups || [];
        const hasSpecialGroup = groups.some(group => group.group === 'Special');
        const hasMileageGroup = groups.some(group => group.group === 'Mileage');
        const offerWithIndex = {...offer, originalIndex};

        if (hasSpecialGroup) {
            categorized.discounted.push(offerWithIndex);
        } else if (hasMileageGroup) {
            categorized.mileage.push(offerWithIndex);
        } else {
            categorized.regular.push(offerWithIndex);
        }
    });

    return categorized;
};

const sortOffersByPrice = (offers) => offers.sort((a, b) => {
    const priceA = a.price || 0;
    const priceB = b.price || 0;
    return priceA - priceB;
});

const DISABLE_SIMULATOR_FOLLOW_UP_CALLS = process.env.REACT_APP_DISABLE_SIMULATOR_FOLLOW_UP_CALLS;
const ALLOWED_FOLLOW_UP_RECORD_TYPES = 'Car_Insurance';

const SimulatorLayout = React.memo(({
    formikHandleChange,
    formikSetField,
    formikValues,
    formikValidateFrom,
    formikSetTouched,
    submitCallback,
    offers,
    onSelectOffer,
    processWithoutOffer,
    openModal,
    modalHeader,
    updating,
    closeModalCallback,
    setCityHasStreets,
    setLicenseNumberValidationCallback,
    currentStreetAndBuildingValidation,
    currentLicenseNumberValidation,
    coversTitles,
    loaderState,
    setIsOffersValuesChanged,
    isOffersValuesChanged,
    agent,
}) => {
    const intl = useIntl();
    const { classes } = useStyles();
    const messages = (id) => intl.formatMessage({ id });
    const [openOfferIndex, setOpenOfferIndex] = useState(0);
    const [openCoversOfferId, setOpenCoversOfferId] = useState(null);
    const [autoInfo, setAutoInfo] = useState(formikValues.autoInfo || null);
    const preferredOffersList = {};
    const isOpportunity = useRef(new URLSearchParams(window.location.search).get('id')?.slice(0, 3) === '006');
    const isAgentAllowedToFollowupCall = agent ? agent.isFollowUpEnabled : true;
    const isEnableFollowUpCalls =
        formikValues.recordTypeDeveloperName === ALLOWED_FOLLOW_UP_RECORD_TYPES &&
        !isOpportunity.current &&
        DISABLE_SIMULATOR_FOLLOW_UP_CALLS !== 'true' &&
        Boolean(isAgentAllowedToFollowupCall);
    const currentDay = Moment();
    const [isFollowUpLoading, setIsFollowUpLoading] = useState(false);
    const [maxDate, setMaxDate] = useState(null);

    const [categorizedOffers, setCategorizedOffers] = useState({
        discounted: [],
        mileage: [],
        regular: [],
    });

    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isPurchaseLoading, setIsPurchaseLoading] = useState(false);
    const [showDataChangedError, setShowDataChangedError] = useState(false);
    const wasUpdating = useRef(false);

    // Reset purchase loading when parent finishes updating
    useEffect(() => {
        if (updating) {
            wasUpdating.current = true;
        } else if (wasUpdating.current && isPurchaseLoading) {
            setIsPurchaseLoading(false);
            wasUpdating.current = false;
        }
    }, [updating, isPurchaseLoading]);

    const getDisplayedOffers = (offersList) => offersList;

    const handleCoversToggle = (offerId, isOpen) => {
        if (isOpen) {
            setOpenCoversOfferId(offerId);
        } else {
            setOpenCoversOfferId(null);
        }
    };

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const handleDateChange = (date) => {
        formikSetField('followupCallDate', date);
    };

    const updateFollowUpCallRandomDate = async (isNextMonthButton) => {
        const shouldSkipGettingFollowUp =
            !isAgentAllowedToFollowupCall ||
            !isEnableFollowUpCalls ||
            !formikValues.startDate && !isNextMonthButton;

        if (shouldSkipGettingFollowUp) {
            return;
        }
        setIsFollowUpLoading(true);
        getMaxDate();

        const { startDate } = formikValues;
        const result = await getFollowUpCallRandomDate({
            policyStartDate: startDate,
        });
        handleDateChange(result.data);
        setIsFollowUpLoading(false);
    };

    const getMaxDate = () => {
        const { startDate } = formikValues;
        const startDateMoment = Moment(startDate);

        const isSameDayPolicy = startDateMoment.isSame(currentDay, 'day');
        if (isSameDayPolicy) {
            const endOfTodayWorkingDay = currentDay.clone().endOf('day').hour(16).minute(59);
            const nowPlus2Hours = currentDay.clone().add(2, 'hours');

            if (nowPlus2Hours.isAfter(endOfTodayWorkingDay)) {
                setMaxDate(startDateMoment.clone().add(1, 'days').hour(15));
                return;
            }

            setMaxDate(endOfTodayWorkingDay);
            return;
        }

        const isFirstOfTheMonth = startDateMoment.date() === 1;
        if (isFirstOfTheMonth) {
            setMaxDate(startDateMoment);
        }

        const differentDays = startDateMoment.diff(currentDay.startOf('day'), 'days');
        if (differentDays > 7) {
            setMaxDate(currentDay.add(7, 'days'));
        } else {
            setMaxDate(startDateMoment);
        }
    };

    useEffect(() => {
        formikSetField('isOpportunity', isOpportunity.current);
        formikSetField('isAgentAllowedToFollowupCall', isAgentAllowedToFollowupCall);
        formikSetField('isEnableFollowUpCalls', isEnableFollowUpCalls);
        formikSetField('allowSimulatorWithoutLicenseNumber', agent.allowSimulatorWithoutLicenseNumber === 'Yes');
        if (isEnableFollowUpCalls) {
            updateFollowUpCallRandomDate();
        }
    }, []);

    const logoSrc = getAgentLogo(agent);

    useEffect(() => {
        if (offers.length > 0) {
            const validOffers = offers.filter(offer => checkFullCoverIsProtected(offer));
            const categorized = categorizeOffers(validOffers);

            // Sort each category by price
            categorized.regular = sortOffersByPrice(categorized.regular);
            categorized.mileage = sortOffersByPrice(categorized.mileage);
            categorized.discounted = sortOffersByPrice(categorized.discounted);

            setCategorizedOffers(categorized);
        }
    }, [offers]);

    const handleSimulatorChange = (isChanged, fieldName) => {
        if (fieldName && ALLOW_FIELDS_CHANGE_AFTER_GET_OFFER[fieldName]) {
            return;
        }

        setIsOffersValuesChanged(isChanged);
    };

    const handleSetFromikFieldValue = (fieldName, value) => {
        handleSimulatorChange(true, fieldName);
        formikSetField(fieldName, value);
    };

    const handleFormikFieldChange = (event) => {
        handleSimulatorChange(true, event.target.name);
        formikHandleChange(event);
    };

    const handleSubmitStep = async () => {
        handleSimulatorChange(false);
        formikSetField('canPurchaseStep', false);
        const errors = await formikValidateFrom();

        const { buildingNumber, street, ...restErrors } = errors;
        if (Object.keys(restErrors).length !== 0) {
            const firstError = Object.keys(errors)[0];
            const firstErrorElement = document.getElementsByName(firstError)[0];
            if (firstErrorElement) {
                firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
            }
            Object.keys({
                ...simulatorValidations,
                ...currentLicenseNumberValidation,
            }).forEach(field => formikSetTouched(field, true));
            return;
        }

        formikSetField('canPurchaseStep', true);

        setSimulatorOffersFields({ ...formikValues, autoInfo }, false);

        setIsSidebarOpen(false);

        submitCallback();
    };

    const handlePurchaseStep = async (offer, index, scrollToError = false) => {
        formikSetField('canPurchaseStep', true);
        const errors = await formikValidateFrom();

        // Check errors for streets and building number before purchase
        if (Object.keys(errors).length !== 0) {
            Object.keys({
                ...simulatorValidations,
                ...currentStreetAndBuildingValidation,
            }).forEach(field => formikSetTouched(field, true));

            // Open sidebar so user can see validation errors
            setIsSidebarOpen(true);

            // Scroll to first error field when called from sidebar
            if (scrollToError) {
                const firstErrorField = Object.keys(errors)[0];
                const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
                if (errorElement) {
                    errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }
            return;
        }

        if (formikValues.followupCallDate) {
            formikValues.followupCallDate = new Date(formikValues.followupCallDate).toISOString();
        }

        setSimulatorOffersFields({ ...formikValues, autoInfo }, false);

        setIsSidebarOpen(false);

        // Show error overlay if values changed instead of proceeding
        if (isOffersValuesChanged) {
            setShowDataChangedError(true);
            return;
        }

        setIsPurchaseLoading(true);

        if (!offer) {
            processWithoutOffer();
            return;
        }
        onSelectOffer(offer, index);
    };

    const onChangePreferredOffer = (offer, isPreferredOffer) => {
        if (preferredOffersList[offer.productLineId] !== undefined) {
            if (!isPreferredOffer) {
                delete preferredOffersList[offer.productLineId];
            }
        } else if (isPreferredOffer) {
            preferredOffersList[offer.productLineId] = offer;
        }
    };

    return (
        <Grid container spacing={0}>
            <Grid className={classes.localHeader} size={12}>
                <Grid container spacing={0}>

                    <Grid size={5}>
                        <Button
                            onClick={toggleSidebar}
                            variant='outlined'
                            className={classes.toggleButton}
                        >
                            {isSidebarOpen ? <CloseIcon /> : <TuneIcon />}
                            {isSidebarOpen ? messages('simulator.close') : messages('simulator.title.underwriting_settings')}
                        </Button>
                    </Grid>
                    <Grid size={6} container justifyContent='flex-end'>
                        {agent ? <img src={logoSrc} alt={messages('validation.wobi_home')} /> : <img src={WobiLogo} alt={messages('validation.wobi_home')} />}
                    </Grid>
                    <Grid />
                </Grid>
                {/* Loader for getting offers */}
                <div className={`${classes.loaderContainer} ${loaderState ? '' : 'hide'}`}>
                    <CircularProgress size={40} />
                </div>
            </Grid>
            <Grid size={12} style={{ padding: '110px 7px 70px 0' }}>
                <Grid container spacing={0}>
                    <Grid size={12} className={classes.offersWrapper}>
                        {/* Overlay for purchase loading */}
                        {isPurchaseLoading && (
                            <div className={classes.purchaseLoadingOverlay}>
                                <Paper style={{ padding: 30, textAlign: 'center' }}>
                                    <CircularProgress size={40} />
                                    <Typography style={{ marginTop: 16 }}>{messages('simulator.updating_product')}</Typography>
                                </Paper>
                            </div>
                        )}
                        {/* Overlay for data changed error */}
                        {showDataChangedError && (
                            <div className={classes.purchaseLoadingOverlay}>
                                <Paper style={{padding: 30, textAlign: 'center'}}>
                                    <WarningAmberIcon style={{fontSize: 40}} color='primary' />
                                    <Typography style={{marginTop: 16}}>{messages('simulator.offer.data_was_changed')}</Typography>
                                    <Button
                                        style={{marginTop: 16}}
                                        variant='contained'
                                        color='primary'
                                        onClick={() => {
                                            setShowDataChangedError(false);
                                            setIsSidebarOpen(true);
                                        }}
                                    >
                                        {messages('simulator.getOffers')}
                                    </Button>
                                </Paper>
                            </div>
                        )}
                        <Grid container spacing={4}>
                            {/* Right Column - Regular */}
                            <Grid size={4}>
                                <Paper elevation={0} className={classes.offerColumn}>
                                    <div className={classes.offerColumnHeaderSticky}>
                                        <Typography variant='h5' component='h3'>
                                            {messages('simulator.regular')}
                                            {' '}
                                        </Typography>
                                        <Typography variant='caption' color='textSecondary'>
                                            {categorizedOffers.regular.length}
                                            {' '}
                                            {messages('simulator.offers_count')}
                                        </Typography>
                                    </div>
                                    {getDisplayedOffers(categorizedOffers.regular).map((offer, index) => {
                                        const offerId = `regular-${index}`;
                                        return (
                                            <Grid size={12} key={offerId} className={`${updating ? classes.wrapperDisabled : ''}`} style={{ marginBottom: 24 }}>
                                                <Offer
                                                    titles={coversTitles}
                                                    setIsOpen={() => setOpenOfferIndex(index)}
                                                    onSelectOffer={() => handlePurchaseStep(offer, offer.originalIndex)}
                                                    onChangePreferredOffer={(isPreferredOffer) => onChangePreferredOffer(offer, isPreferredOffer)}
                                                    data={{
                                                        ...offer,
                                                        pdfLink: `/policies/${offer.productLineId}/policy.pdf`,
                                                    }}
                                                    isOpen={true || openOfferIndex === index}
                                                    isCoversSectionExpanded={openCoversOfferId === offerId}
                                                    onCoversToggle={(isOpen) => handleCoversToggle(offerId, isOpen)}
                                                    noLeft
                                                    updating={updating}
                                                    agent={agent}
                                                />
                                            </Grid>
                                        );
                                    })}
                                </Paper>
                            </Grid>
                            {/* Middle Column - Mileage */}
                            <Grid size={4}>
                                <Paper elevation={0} className={classes.offerColumn}>
                                    <div className={classes.offerColumnHeaderSticky}>
                                        <Typography variant='h5' component='h3'>{messages('simulator.mileage_discount')}</Typography>
                                        <Typography variant='caption' color='textSecondary'>
                                            {categorizedOffers.mileage.length}
                                            {' '}
                                            {messages('simulator.offers_count')}
                                        </Typography>
                                    </div>
                                    {getDisplayedOffers(categorizedOffers.mileage).map((offer, index) => {
                                        const offerId = `mileage-${index}`;
                                        return (
                                            <Grid size={12} key={offerId} className={`${updating ? classes.wrapperDisabled : ''}`} style={{ marginBottom: 24 }}>
                                                <Offer
                                                    titles={coversTitles}
                                                    setIsOpen={() => setOpenOfferIndex(index)}
                                                    onSelectOffer={() => handlePurchaseStep(offer, offer.originalIndex)}
                                                    onChangePreferredOffer={(isPreferredOffer) => onChangePreferredOffer(offer, isPreferredOffer)}
                                                    data={{
                                                        ...offer,
                                                        pdfLink: `/policies/${offer.productLineId}/policy.pdf`,
                                                    }}
                                                    isOpen={true || openOfferIndex === index}
                                                    isCoversSectionExpanded={openCoversOfferId === offerId}
                                                    onCoversToggle={(isOpen) => handleCoversToggle(offerId, isOpen)}
                                                    noLeft
                                                    updating={updating}
                                                    agent={agent}
                                                />
                                            </Grid>
                                        );
                                    })}
                                </Paper>
                            </Grid>
                            {/* Left Column - Discounted */}
                            <Grid size={4}>
                                <Paper elevation={0} className={classes.offerColumn}>
                                    <div className={classes.offerColumnHeaderSticky}>
                                        <Typography variant='h5' component='h3'>{messages('simulator.deductible_discount')}</Typography>
                                        <Typography variant='caption' color='textSecondary'>
                                            {categorizedOffers.discounted.length}
                                            {' '}
                                            {messages('simulator.offers_count')}
                                        </Typography>
                                    </div>
                                    {getDisplayedOffers(categorizedOffers.discounted).map((offer, index) => {
                                        const offerId = `discounted-${index}`;
                                        return (
                                            <Grid size={12} key={offerId} className={`${updating ? classes.wrapperDisabled : ''}`} style={{ marginBottom: 24 }}>
                                                <Offer
                                                    titles={coversTitles}
                                                    setIsOpen={() => setOpenOfferIndex(index)}
                                                    onSelectOffer={() => handlePurchaseStep(offer, offer.originalIndex)}
                                                    onChangePreferredOffer={(isPreferredOffer) => onChangePreferredOffer(offer, isPreferredOffer)}
                                                    data={{
                                                        ...offer,
                                                        pdfLink: `/policies/${offer.productLineId}/policy.pdf`,
                                                    }}
                                                    isOpen={true || openOfferIndex === index}
                                                    isCoversSectionExpanded={openCoversOfferId === offerId}
                                                    onCoversToggle={(isOpen) => handleCoversToggle(offerId, isOpen)}
                                                    noLeft
                                                    updating={updating}
                                                    agent={agent}
                                                />
                                            </Grid>
                                        );
                                    })}
                                </Paper>
                            </Grid>
                        </Grid>
                    </Grid>
                    <ModalContainer showCloseButton={isOpportunity.current}
                        openModal={openModal} closeModalCallback={closeModalCallback} modalHeader={modalHeader}
                        showDefaultHeader={false} />
                </Grid>
                {/* Underwriting drawer */}
                <Drawer
                    anchor='left'
                    open={isSidebarOpen}
                    onClose={toggleSidebar}
                    ModalProps={{ keepMounted: true }}
                    BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.5)' } }}
                    PaperProps={{
                        style: {
                            width: '60vw',
                        },
                    }}
                >
                    <div className={classes.sidebarContent}>
                        <div className={classes.sidebarToolbar}>
                            <Button
                                size='small'
                                variant='contained'
                                disabled={
                                    formikValues.licenseRevocation.toString() === '-1' ||
                                    formikValues.licenseRevocation.toString() === '2' || loaderState
                                }
                                onClick={handleSubmitStep}
                            >
                                {messages('simulator.getOffers')}
                                <ArrowBackIcon />
                            </Button>

                            <Button
                                variant='outlined'
                                size='small'
                                disabled={
                                    formikValues.licenseRevocation.toString() === '-1' ||
                                    formikValues.licenseRevocation.toString() === '2' || loaderState
                                }
                                onClick={() => handlePurchaseStep(null, null, true)}
                            >
                                {messages('simulator.saveWithoutOffer')}
                                <ArrowBackIcon />
                            </Button>

                            <Button
                                onClick={toggleSidebar}
                                className={classes.closeSidebarButton}
                                variant='contained'
                            >
                                <CloseIcon />
                            </Button>

                            <SmallSelect
                                width='140px'
                                value={formikValues.policyType}
                                onChange={(event) => handleSetFromikFieldValue('policyType', event.target.value)}
                                name='policyType'
                                label={intl.formatMessage({ id: 'simulator.policyType' })}
                            >
                                {policyTypes.map(item => (
                                    <MenuItem key={item.value} value={item.value}>{item.text}</MenuItem>
                                ))}
                            </SmallSelect>
                        </div>

                        <Divider />

                        {loaderState && (
                            <div className={classes.sidebarLoadingBanner}>
                                <CircularProgress size={20} color='inherit' />
                                {messages('simulator.offers_loading_warning')}
                            </div>
                        )}

                        <div className={`${classes.sidebarBody} ${loaderState ? classes.sidebarBodyDisabled : ''}`}>
                            <GeneralDetails
                                isOpportunity={isOpportunity.current}
                                formikHandleChange={handleFormikFieldChange}
                                formikSetField={handleSetFromikFieldValue}
                                formikValues={formikValues}
                                updateFollowUpCallRandomDate={updateFollowUpCallRandomDate}
                            />
                            {isEnableFollowUpCalls ? (
                                <FollowUpCall
                                    formikHandleChange={handleFormikFieldChange}
                                    formikSetField={handleSetFromikFieldValue}
                                    formikValues={formikValues}
                                    handleDateChange={handleDateChange}
                                    isAgentAllowedToFollowupCall={isAgentAllowedToFollowupCall}
                                    isEnableFollowUpCalls={isEnableFollowUpCalls}
                                    isFollowUpLoading={isFollowUpLoading}
                                    maxDate={maxDate}
                                    minDate={currentDay}
                                />
                            ) : null}
                            <AutoInfoDetails
                                autoInfo={autoInfo}
                                setAutoInfo={setAutoInfo}
                                formikHandleChange={handleFormikFieldChange}
                                formikSetField={handleSetFromikFieldValue}
                                formikValues={formikValues}
                                setLicenseNumberValidationCallback={setLicenseNumberValidationCallback}
                                agentDeveloperName={agent?.developerName}
                            />
                            <DriversDetails
                                formikHandleChange={handleFormikFieldChange}
                                formikSetField={handleSetFromikFieldValue}
                                formikValues={formikValues}
                            />
                            <PassedInsurance
                                formikSetField={handleSetFromikFieldValue}
                                formikValues={formikValues}
                            />
                            <Claims
                                formikSetField={handleSetFromikFieldValue}
                                formikValues={formikValues}
                            />
                            <MoreDetails
                                formikHandleChange={handleFormikFieldChange}
                                formikSetField={handleSetFromikFieldValue}
                                formikValues={formikValues}
                                handleSubmitStep={handleSubmitStep}
                                setCityHasStreets={setCityHasStreets}
                            />
                        </div>
                    </div>
                </Drawer>
            </Grid>
        </Grid>
    );
});

SimulatorLayout.propTypes = {
    agent: PropTypes.object,
    closeModalCallback: PropTypes.func,
    coversTitles: PropTypes.array,
    currentLicenseNumberValidation: PropTypes.object,
    currentStreetAndBuildingValidation: PropTypes.object,
    formikHandleChange: PropTypes.func,
    formikSetField: PropTypes.func,
    formikSetTouched: PropTypes.func,
    formikValidateFrom: PropTypes.func,
    formikValues: PropTypes.object,
    isNewPotential: PropTypes.bool,
    loaderState: PropTypes.bool,
    lowestPrice: PropTypes.number,
    modalHeader: PropTypes.string,
    offers: PropTypes.array,
    onSelectOffer: PropTypes.func,
    openModal: PropTypes.bool,
    potentialId: PropTypes.string,
    processWithoutOffer: PropTypes.func,
    recordTypeDeveloperName: PropTypes.string,
    setCityHasStreets: PropTypes.func,
    setIsOffersValuesChanged: PropTypes.func,
    isOffersValuesChanged: PropTypes.bool,
    setLicenseNumberValidationCallback: PropTypes.func,
    setStreetAndBuildingNumberValidationCallback: PropTypes.func,
    submitCallback: PropTypes.func,
    updatePreferredCallback: PropTypes.func,
    updating: PropTypes.bool,
};

const claimWithNoPastInsuranceMsg = 'לא ניתן להזין פרטי תביעה ללא עבר ביטוחי';

// must stay a function - a module level Moment() freezes at bundle load and goes stale in a long lived tab
const now = () => Moment();

export const simulatorValidations = {
    canPurchaseStep: yup.boolean(),
    city: yup.string().required('שדה חובה'),
    driversCount: yup.string().required('שדה חובה'),
    fcw: yup.string().required('שדה חובה'),
    firstName: yup.string().when('canPurchaseStep', {
        is: (value) => value,
        then: (schema) => schema.required('שדה חובה'),
    }),

    followupPhoneNumber: yup.string().nullable().when([
        'canPurchaseStep',
        'isEnableFollowUpCalls',
    ], {
        is: (
            canPurchaseStep,
            isEnableFollowUpCalls,
        ) => {
            if (!isEnableFollowUpCalls) {
                return false;
            }
            return canPurchaseStep || false;
        },
        then: (schema) =>
            schema
                .length(10, 'יש להקליד עשר ספרות בלבד')
                .matches(/^0\d{9}$/, 'מספר טלפון - ספרות בלבד')
                .required('שדה חובה'),
    }),
    haveAnotherCar: yup.string().when('policyType',
        {
            is: (value) => value === ProductType.FullCover,
            then: (schema) => schema.required('שדה חובה'),
        }),
    injuryClaims_1: yup.number().typeError('שדה חובה').required('שדה חובה'),

    injuryClaims_2: yup.number().typeError('שדה חובה').required('שדה חובה'),

    injuryClaims_3: yup.number().typeError('שדה חובה').required('שדה חובה'),

    insurance_1: yup.string().required('שדה חובה'),

    insurance_2: yup.string().required('שדה חובה'),

    insurance_3: yup.string().required('שדה חובה'),

    israeliId: yup.string().nullable().when(['canPurchaseStep', 'isOpportunity'], {
        is: (canPurchaseStep, isOpportunity) => {
            if (isOpportunity) {
                return false;
            } else {
                return canPurchaseStep;
            }
        },
        then: () => validIsraeliId,
    }),

    isSaleInProgress: yup.boolean().nullable().when([
        'canPurchaseStep',
        'isOpportunity',
        'isAgentAllowedToFollowupCall',
        'isEnableFollowUpCalls',
    ], {
        is: (
            canPurchaseStep,
            isOpportunity,
            isAgentAllowedToFollowupCall,
            isEnableFollowUpCalls,
        ) => {
            const IS_SIMULATOR_FOLLOWUP_FIELD_OPTIONAL = process.env.REACT_APP_IS_SIMULATOR_FOLLOWUP_FIELD_OPTIONAL;
            const isSimulatorFollowupFieldOptional = IS_SIMULATOR_FOLLOWUP_FIELD_OPTIONAL === 'true';
            if (isOpportunity || isSimulatorFollowupFieldOptional || !isAgentAllowedToFollowupCall || !isEnableFollowUpCalls) {
                return false;
            }
            return canPurchaseStep || false;
        },
        then: (schema) => schema.required('שדה חובה'),
    }),
    lastName: yup.string().when('canPurchaseStep', {
        is: (value) => value,
        then: (schema) => schema.required('שדה חובה'),
    }),
    ldw: yup.string().required('שדה חובה'),
    licenseNumber: yup.string().nullable().when(['canPurchaseStep', 'allowSimulatorWithoutLicenseNumber'], {
        is: (canPurchaseStep, allowSimulatorWithoutLicenseNumber) => canPurchaseStep && !allowSimulatorWithoutLicenseNumber,
        then: (schema) => schema.required('שדה חובה'),
    }),
    licenseRevocation: yup.number().required('שדה חובה'),
    mainDriverBirthDate: yup.string().nullable().min(1).when(['driversCount', 'isYoungDriverIsMain'], {
        is: (driversCount, isYoungDriverIsMain) => driversCount === '-1' && isYoungDriverIsMain === 'no',
        then: (schema) => schema.required('שדה חובה'),
    }),

    mainDriverSeniority: yup.string().when(['driversCount', 'isYoungDriverIsMain'], {
        is: (driversCount, isYoungDriverIsMain) => driversCount === '-1' && isYoungDriverIsMain === 'no',
        then: (schema) => schema.required('שדה חובה'),
    }),

    // youngDriverBirthDate: yup.date().typeError('שדה חובה').required(),
    model: yup.string().typeError('שדה חובה').required('שדה חובה'),

    pledged: yup.string().required('שדה חובה'),

    policyType: yup.string().required('שדה חובה'),

    propClaims_1: yup.number().required('שדה חובה').test(
        'propClaims_1',
        claimWithNoPastInsuranceMsg,
        function () {
            return this.parent.insurance_1 === '0' ? this.parent.propClaims_1 === 0 : true;
        },
    ).test(
        'propClaims_1',
        'לא ניתן להמשיך עם יותר משתי תביעות',
        function () {
            return this.parent.propClaims_1 +
                this.parent.propClaims_2 +
                this.parent.propClaims_3 < 3;
        },
    ),
    propClaims_2: yup.number().required('שדה חובה').test(
        'propClaims_2',
        claimWithNoPastInsuranceMsg,
        function () {
            return this.parent.insurance_2 === '0' ? this.parent.propClaims_2 === 0 : true;
        },
    ).test(
        'propClaims_2',
        'לא ניתן להמשיך עם יותר משתי תביעות',
        function () {
            return this.parent.propClaims_1 +
                this.parent.propClaims_2 +
                this.parent.propClaims_3 < 3;
        },
    ),
    propClaims_3: yup.number().required('שדה חובה').test(
        'propClaims_3',
        claimWithNoPastInsuranceMsg,
        function () {
            return this.parent.insurance_3 === '0' ? this.parent.propClaims_3 === 0 : true;
        },
    ).test(
        'propClaims_3',
        'לא ניתן להמשיך עם יותר משתי תביעות',
        function () {
            return this.parent.propClaims_1 +
                this.parent.propClaims_2 +
                this.parent.propClaims_3 < 3;
        },
    ),
    saturdayDriving: yup.string().required('שדה חובה'),
    startDate: yup.mixed().required('שדה חובה')
        .test('validDate', 'תאריך שגוי', (date) => conditions.isValidDate(date))
        .test('aboveMin', 'לא ניתן לבחור בתאריך שכבר חלף',
            (date) => conditions.isAboveMin(date, now())),

    /*
  street: yup.string().when('canPurchaseStep', {
    is: (value) => value,
    then: yup.string().required('שדה חובה'),
  }),
  */

    subModel: yup.string().typeError('שדה חובה').required('שדה חובה'),
    year: yup.string().typeError('שדה חובה').required('שדה חובה'),
    youngDriverGender: yup.string().notOneOf(['0'], 'שדה חובה').required('שדה חובה'),
    youngDriverSeniority: yup.string().test(
        'youngDriverSeniority',
        'ותק לא תקין',
        function () {
            const firstYearSeniorityAgeYears = 16;
            const firstYearSeniorityAgeMonths = 9;
            return Moment(this.parent.youngDriverBirthDate)
                .add(firstYearSeniorityAgeYears + Number(this.parent.youngDriverSeniority), 'years')
                .add(firstYearSeniorityAgeMonths, 'months')
                .isBefore();
        },
    ).required('שדה חובה'),
};

export default SimulatorLayout;
